
import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import AmbientesSection from '../../components/feature/AmbientesSection';
import AmbientesDisponiblesSection from '../../components/feature/AmbientesDisponiblesSection';
import { useSEO } from '../../hooks/useSEO';

interface Ambiente {
  id: number;
  imagen: string;
  alt: string;
}

const ambientes: Ambiente[] = [
  { id: 1, imagen: '/images/ambientes/opcion1.png', alt: 'Muebles de baño moderno con diseño contemporáneo y acabados premium' },
  { id: 2, imagen: '/images/ambientes/opcion2.png', alt: 'Muebles de baño de lujo navamuel con acabados de alta calidad' },
  { id: 3, imagen: '/images/ambientes/opcion3.png', alt: 'Muebles de baño minimalista con líneas limpias y diseño funcional' },
  { id: 4, imagen: '/images/ambientes/opcion4.png', alt: 'Muebles de baño clásico con toques de modernidad personalizados' },
  { id: 5, imagen: '/images/ambientes/opcion5.png', alt: 'Muebles de baño funcional y elegante con maderas naturales premium' },
  { id: 6, imagen: '/images/ambientes/opcion6.png', alt: 'Muebles de baño con iluminación de diseño y acabados premium' },
  { id: 7, imagen: '/images/ambientes/opcion7.png', alt: 'Muebles de baño con acabados naturales premium personalizados navamuel' },
];

export default function AmbientesPage() {
  const [indiceCarrusel, setIndiceCarrusel] = useState(0 );
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isFading, setIsFading] = useState(false);

  useSEO({
    title: 'Muebles de Baño a Medida | Fábrica de Muebles de Baño Navamuel Toledo',
    description: 'Somos fabricantes de muebles de baño a medida en Toledo. Descubre nuestro catálogo de ambientes, diseños modernos, clásicos y de lujo con maderas naturales, porcelánicos y acabados premium. Personaliza tu baño con la mejor fábrica de muebles de baño.',
    keywords: 'muebles de baño, fábrica de muebles de baño, fabricantes de muebles de baño, muebles de baño a medida, muebles baño toledo, muebles baño modernos, muebles baño de lujo, muebles baño maderas naturales, muebles baño suspendidos, ambientes baño diseño, baños a medida, muebles baño personalizados, fabricantes baños, distribuidor muebles baño',
    url: 'https://www.mueblesnavamuel.com/muebles-de-bano'
  });

  useEffect(() => {
    const interval = setInterval(() => {
      siguienteAmbiente();
    }, 3000);

    return () => clearInterval(interval);
  }, [indiceCarrusel]);

  const siguienteAmbiente = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setIndiceCarrusel((prev) => (prev + 1) % ambientes.length);
    setTimeout(() => setIsTransitioning(false), 700);
  };

  const irASlide = (index: number) => {
    if (isTransitioning || index === indiceCarrusel) return;
    setIsTransitioning(true);
    setIsFading(true);
    setIndiceCarrusel(index);
    setTimeout(() => {
      setIsTransitioning(false);
      setIsFading(false);
    }, 700);
  };

  const obtenerIndiceVisible = (offset: number) => {
    return (indiceCarrusel + offset + ambientes.length) % ambientes.length;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section with SEO optimized H1 */}
      <div className="bg-white min-h-screen flex items-center">
        <div className="w-full mx-auto text-center" style={{ padding: 'clamp(2rem, 4vh, 6rem) clamp(1rem, 3vw, 5rem)' }}>
          <h1 
            className="font-light text-gray-900" 
            style={{ 
              fontFamily: 'Playfair Display, serif',
              fontSize: 'clamp(2.5rem, 5vw, 7rem)',
              marginBottom: 'clamp(1rem, 2vh, 3rem)'
            }}
          >
            AMBIENTES
          </h1>
          <p 
            className="text-gray-600 max-w-[1400px] mx-auto leading-relaxed" 
            style={{ 
              fontFamily: 'Cormorant Garamond, serif',
              fontSize: 'clamp(1rem, 1.2vw, 1.5rem)',
              padding: '0 clamp(1rem, 2vw, 3rem)'
            }}
          >
            Descubre nuestro catálogo de <strong>ambientes y diseños de muebles de baño</strong> crafted por la mejor <strong>fábrica de muebles de baño en Toledo</strong>. Cada espacio refleja un estilo único, combinando funcionalidad y estética para crear espacios de baño excepcionales.
            <br /><br />
            Como <strong>fabricantes de muebles de baño a medida</strong>, nos adaptamos a las necesidades de cada proyecto. Personaliza tu baño con diseños modernos, clásicos o de lujo. Elige entre maderas naturales premium, porcelánicos y acabados exclusivos. Encuentra la inspiración perfecta para tu proyecto.
          </p>
        </div>
      </div>

      {/* Info Section with H2 for SEO */}
      <section className="bg-gray-50 py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-light text-center mb-12 text-gray-900">
            ¿Por qué elegir nuestros muebles de baño?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <h3 className="text-xl font-semibold mb-3 text-gray-800">Fabricantes Especializados</h3>
              <p className="text-gray-600">
                Somos una fábrica de muebles de baño con años de experiencia fabricando muebles de baño personalizados y a medida para clientes exigentes.
              </p>
            </div>
            <div className="text-center">
              <h3 className="text-xl font-semibold mb-3 text-gray-800">Diseño a Medida</h3>
              <p className="text-gray-600">
                Nuestros muebles de baño personalizados se adaptan perfectamente a tus necesidades. Cada mueble de baño es fabricado con precisión y cuidado.
              </p>
            </div>
            <div className="text-center">
              <h3 className="text-xl font-semibold mb-3 text-gray-800">Materiales Premium</h3>
              <p className="text-gray-600">
                Maderas naturales, porcelánicos y acabados de lujo. Todos nuestros muebles de baño destacan por su calidad y durabilidad excepcional.
              </p>
            </div>
          </div>
        </div>
      </section>

      <AmbientesSection />
      
      <AmbientesDisponiblesSection />

      {/* SEO Content Section */}
      <section className="bg-white py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-8">
            Catálogo de Ambientes - Muebles de Baño Navamuel
          </h2>
          <div className="prose max-w-none text-gray-700">
            <p className="mb-6">
              En Navamuel somos <strong>fabricantes de muebles de baño</strong> especializados en la creación de espacios únicos. Nuestro extenso catálogo de <strong>ambientes de baño</strong> te permite inspirarte y personalizar completamente tu proyecto.
            </p>
            <p className="mb-6">
              Desde <strong>muebles de baño modernos</strong> con líneas contemporáneas, hasta <strong>muebles de baño de lujo</strong> con acabados exclusivos. Todos nuestros <strong>muebles de baño a medida</strong> son fabricados con maderas naturales premium como ébano, roble y nogal.
            </p>
            <p className="mb-6">
              Como <strong>fábrica de muebles de baño en Toledo</strong>, ofrecemos soluciones integrales: muebles de baño suspendidos, porcelánicos, resinas minerales y todo lo que necesitas para crear el baño de tus sueños.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
